import { doesDocumentExtendExpiration, } from '../../../documents/index.js';
import { enteredAmount, isAssociationManaged, isAssociationMirror, } from '../../case-vouchers/index.js';
import { CompoundingPeriod, daysAYear, ImposeInterestFrequency, VoucherCategory, VoucherTypeSetting, } from '../../vouchers/index.js';
import { isCreditNote, isFixedInterest, isPayment, } from '../../vouchers/matcher.js';
import { isCalculatedInterest } from '../../vouchers/setup.js';
import { BreakpointType, COMPOUNDING_PERIOD_TO_BREAKPOINT_TYPE, getInterestRatesGen, OPEN_ENDED, } from '../interest/common.js';
import { addParsedInterestPeriods, } from '../interest/periods.js';
import { dayIn, monthStart, MS_PER_DAY, NEVER, nextMonthStart, quarterStart, startOf, } from './dates.js';
const kindOf = (voucher) => {
    if (isCalculatedInterest(voucher)) {
        return 'interest';
    }
    const amount = enteredAmount(voucher);
    if (amount < 0) {
        return 'debt';
    }
    return amount > 0 ? 'credit' : 'none';
};
const tierOf = (policy, category) => {
    const index = policy.categoryTiers.findIndex((tier) => tier.includes(category));
    return index === -1 ? 0 : index;
};
export const isAssociationSourceVoucher = (voucher) => (isPayment(voucher) || isCreditNote(voucher)) && !isAssociationMirror(voucher);
// v1's helpers parse days as the UTC instants they start at.
const dayOfStart = (time) => time / MS_PER_DAY;
const interestRule = (voucher) => {
    let rates;
    if (voucher.setting === VoucherTypeSetting.InterestFromTable) {
        rates = getInterestRatesGen(voucher.settingDetails.interestRates.map((rate) => ({
            ...rate,
            time: dayOfStart(rate.time),
        })));
    }
    else if ((voucher.setting === VoucherTypeSetting.InterestPercentage ||
        voucher.setting === VoucherTypeSetting.CompoundInterest) &&
        typeof voucher.interest === 'number') {
        const rate = voucher.interest;
        rates = () => [{ rate }];
    }
    else {
        rates = () => {
            throw new Error(`Interest type not handled (setting: ${voucher.setting}, voucher id: ${voucher.caseVoucherId})`);
        };
    }
    const periods = (voucher.parsedInterestPeriods ?? []).map((period) => ({
        ...period,
        rateFrom: dayOfStart(period.rateFrom),
        rateTo: period.rateTo === OPEN_ENDED ? NEVER : dayOfStart(period.rateTo),
        voidedAt: period.voidedAt === null ? null : dayOfStart(period.voidedAt),
    }));
    const preventExpiration = voucher.details?.preventInterestExpiration === true ||
        (voucher.interestStartDate != null && voucher.interestEndDate != null);
    return {
        rates,
        daysAYear: daysAYear(voucher),
        startedMonth: voucher.setting === VoucherTypeSetting.InterestPercentage &&
            voucher.settingDetails.imposeInterestFrequency ===
                ImposeInterestFrequency.Monthly,
        compoundingType: voucher.setting === VoucherTypeSetting.CompoundInterest
            ? COMPOUNDING_PERIOD_TO_BREAKPOINT_TYPE[voucher.settingDetails.compoundingPeriod]
            : null,
        start: voucher.parsedInterestStartDate === null
            ? null
            : dayOfStart(voucher.parsedInterestStartDate),
        end: voucher.parsedInterestEndDate === null
            ? null
            : dayOfStart(voucher.parsedInterestEndDate),
        periods,
        voided: periods.flatMap((period, periodIndex) => period.voidedAt === null
            ? []
            : [
                {
                    periodId: period.id,
                    voidedAt: period.voidedAt,
                    index: periodIndex + 1,
                },
            ]),
        refId: voucher.referenceCaseVoucherId,
        expiry: preventExpiration ? 'never' : 'rolling',
    };
};
const debtClock = (policy, day, voucher) => {
    const standard = voucher.category === VoucherCategory.INTEREST
        ? policy.limitation.interest
        : policy.limitation.debt;
    let expiry = 'rolling';
    if (voucher.category === VoucherCategory.TRANSACTION) {
        expiry = 'never';
    }
    else if (voucher.expirationManualSet) {
        expiry =
            voucher.expirationDate == null ? 'never' : day(voucher.expirationDate);
    }
    return {
        periods: {
            ordinary: voucher.expirationLength ?? standard.ordinary,
            foundation: standard.foundation,
        },
        pinned: voucher.expirationManualSet,
        expiry,
        clockFrom: day(voucher.expirationFromDate ?? voucher.date),
        marked: voucher.expired,
    };
};
// Money in and out, as opposed to debt.
const MONEY_CATEGORIES = [
    VoucherCategory.TRANSACTION,
    VoucherCategory.PAYOUT,
    VoucherCategory.OVERPAYMENT,
];
const isDebtEvent = (event) => (event.kind === 'debt' || event.kind === 'interest') &&
    !MONEY_CATEGORIES.includes(event.category);
// The order vouchers happen in. D32: on one day, debt comes before money, so
// a payment pays debt registered the same day whatever the time of day.
export const compareEvents = (a, b) => a.day - b.day ||
    Number(isDebtEvent(b)) - Number(isDebtEvent(a)) ||
    a.instant - b.instant ||
    Number(a.jointOrdered) - Number(b.jointOrdered) ||
    (a.jointOrdered ? a.sourceKey.localeCompare(b.sourceKey) : 0) ||
    a.sequence - b.sequence ||
    a.inputIndex - b.inputIndex;
// The output written to: a copy of the voucher with v1's parsed days. A
// parse cached by an earlier caller is replaced, as it may not be in the
// tenant's timezone.
const outputOf = (dayStart, caseVoucher) => Object.assign(addParsedInterestPeriods(Object.assign({ ...caseVoucher }, {
    rawDate: new Date(caseVoucher.date).getTime(),
    parsedDate: dayStart(caseVoucher.date),
    parsedInterestStartDate: caseVoucher.interestStartDate == null
        ? null
        : dayStart(caseVoucher.interestStartDate),
    parsedInterestEndDate: caseVoucher.interestEndDate == null
        ? null
        : dayStart(caseVoucher.interestEndDate),
}), dayStart), { distribution: [], distributionAmount: 0 });
const voucherEvent = (policy, day, associated, voucher, inputIndex) => {
    const kind = kindOf(voucher);
    const isMirror = isAssociationManaged(voucher);
    const voucherDay = day(voucher.date);
    return {
        caseVoucherId: voucher.caseVoucherId,
        date: voucher.date,
        day: voucherDay,
        instant: voucher.rawDate,
        sequence: voucher.id,
        inputIndex,
        category: voucher.category,
        tier: tierOf(policy, voucher.category),
        kind,
        inputAmount: enteredAmount(voucher),
        refId: voucher.referenceCaseVoucherId,
        isFixedInterest: isFixedInterest(voucher),
        associationId: voucher.associationId,
        isMirror,
        jointOrdered: associated && (isMirror || isAssociationSourceVoucher(voucher)),
        sourceKey: voucher.associationSourceCaseVoucherId ?? voucher.caseVoucherId,
        jointSource: associated && !isMirror && isAssociationSourceVoucher(voucher),
        irrecoverableDay: voucher.irrecoverableDate == null || kind === 'credit' || kind === 'none'
            ? null
            : Math.max(day(voucher.irrecoverableDate), voucherDay),
        markedDay: kind === 'interest' &&
            voucher.expired &&
            voucher.expirationManualSet &&
            voucher.expirationDate != null
            ? Math.max(day(voucher.expirationDate) + 1, voucherDay)
            : null,
        interruptingPayment: kind === 'credit' &&
            !isMirror &&
            voucher.setting === VoucherTypeSetting.Payment &&
            voucher.settingDetails.interruptsLimitationPeriod,
        clock: kind === 'debt' ? debtClock(policy, day, voucher) : null,
        rule: kind === 'interest' ? interestRule(voucher) : null,
    };
};
const COMPOUNDING_STEPS = {
    [CompoundingPeriod.Daily]: { first: (day) => day, next: (day) => day + 1 },
    [CompoundingPeriod.Monthly]: { first: monthStart, next: nextMonthStart },
    [CompoundingPeriod.Quarterly]: {
        first: quarterStart,
        next: (day) => nextMonthStart(nextMonthStart(nextMonthStart(day))),
    },
};
// From the UTC day of the earliest of `vouchers` until `until`, as v1 steps.
const steps = (type, vouchers, until, { first, next }) => {
    if (vouchers.length === 0) {
        return [];
    }
    const earliest = Math.floor(vouchers.reduce((min, voucher) => Math.min(min, voucher.rawDate), Number.POSITIVE_INFINITY) / MS_PER_DAY);
    const breakpoints = [];
    for (let date = first(earliest); date <= until; date = next(date)) {
        breakpoints.push({ date, type });
    }
    return breakpoints;
};
// v1's breakpoint steps: where interest starts, ends and its periods change,
// each compounding date, and on request each month start (see
// CalcOptions.monthStartBreakpoints).
const breakpointsOf = (vouchers, until, monthStarts) => {
    const breakpoints = [];
    for (const voucher of vouchers) {
        if (voucher.parsedInterestStartDate != null) {
            breakpoints.push({
                date: dayOfStart(voucher.parsedInterestStartDate),
                type: BreakpointType.InterestStartDate,
            });
        }
        if (voucher.parsedInterestEndDate != null) {
            breakpoints.push({
                date: dayOfStart(voucher.parsedInterestEndDate),
                type: BreakpointType.InterestEndDate,
            });
        }
        for (const period of voucher.parsedInterestPeriods ?? []) {
            breakpoints.push({
                date: dayOfStart(period.rateFrom),
                type: BreakpointType.InterestPeriodStart,
            });
            if (period.rateTo !== OPEN_ENDED) {
                breakpoints.push({
                    date: dayOfStart(period.rateTo),
                    type: BreakpointType.InterestPeriodEnd,
                });
            }
            if (period.voidedAt !== null) {
                breakpoints.push({
                    date: dayOfStart(period.voidedAt),
                    type: BreakpointType.InterestPeriodVoid,
                });
            }
        }
    }
    // Concatenated, not spread: a long projection has more compounding days
    // than a call takes arguments.
    const compounding = Object.values(CompoundingPeriod).flatMap((period) => steps(COMPOUNDING_PERIOD_TO_BREAKPOINT_TYPE[period], vouchers.filter((voucher) => voucher.setting === VoucherTypeSetting.CompoundInterest &&
        voucher.settingDetails.compoundingPeriod === period), until, COMPOUNDING_STEPS[period]));
    const ordered = breakpoints
        .concat(compounding)
        .toSorted((a, b) => a.date - b.date);
    return monthStarts
        ? ordered
            .concat(steps(BreakpointType.MonthStart, vouchers, until, COMPOUNDING_STEPS[CompoundingPeriod.Monthly]))
            .toSorted((a, b) => a.date - b.date)
        : ordered;
};
export const caseEvents = (policy, until, monthStartBreakpoints, associated, caseVouchers, documents) => {
    const day = dayIn(policy.timezone);
    const dayStart = (date) => startOf(day(date));
    const outputs = caseVouchers.map((caseVoucher) => outputOf(dayStart, caseVoucher));
    const vouchers = outputs
        .map((output, index) => voucherEvent(policy, day, associated, output, index))
        .toSorted(compareEvents);
    return {
        vouchers,
        outputs,
        breakpoints: breakpointsOf(vouchers.map((event) => outputs[event.inputIndex]), until, monthStartBreakpoints),
        foundations: [
            ...new Set(documents
                .filter(doesDocumentExtendExpiration)
                .map((document) => day(document.signedAt ?? document.date))),
        ].toSorted((a, b) => a - b),
    };
};
//# sourceMappingURL=events.js.map